import { useEffect, useRef, useState } from "react";
import styles from "./ScratchGame.module.less";
import DownTime from "../components/DownTime";
import NoticeText from "../components/NoticeText";
import DSBridge from "dsbridge";
import { getGameAudio, getMaterialsByGame } from "@/api/mine";
import { showDeviceModal } from "views/mine/device-manage/components/device-modal";
import { Toast } from "react-vant";
import { useSearchParams } from "react-router-dom";

const ScratchGame = () => {
  const [searchParams] = useSearchParams();
  const [isVisible, setIsVisible] = useState(true);
  const [title, setTitle] = useState("");
  const [scratchImg, setScratchImg] = useState("");
  const [showGuide, setShowGuide] = useState(true);
  const [showScratchLayer, setShowScratchLayer] = useState(true);

  const downTimeRef = useRef(null);
  const isStartRef = useRef(false);
  const canvasRef = useRef(null);
  const isDrawing = useRef(false);
  const lastPoint = useRef({ x: 0, y: 0 });
  const explanationRef = useRef(null);

  const handleClose = () => {
    showDeviceModal({
      message:
        "确定退出课堂练习？中途退出无法获得积分哦~退出后将继续学习课程。",
      onConfirm: () => {
        DSBridge.call("stopSound", "bgm_sound");
        DSBridge.call("exitWebView");
      },
      isShowCancel: true,
      isShowConfirm: true,
      cancelText: "取消",
      confirmText: "退出",
    });
  };

  const handleGetAudio = async (text) => {
    try {
      // const res = await getGameAudio({
      //   character_id: "lily_white",
      //   token: "IMdW6ZWukrH6qrUteWDa6Mzm-SR-zPxW3z1Iq-2xg08.",
      //   text: text,
      // });
      const res = await getGameAudio({
        character_id: searchParams.get("character_id"),
        token: searchParams.get("token"),
        text: text,
      });
      if (res.code === 200 && res.data?.url) {
        return res.data.url;
      }
      throw new Error("Failed to get audio URL");
    } catch (error) {
      throw error;
    }
  };

  const playAudioAndExit = async (explanationText, score = 0) => {
    try {
      const audioUrl = await handleGetAudio(explanationText);
      DSBridge.call(
        "playOnlineSound",
        `${process.env.REACT_APP_API_CDN}/${audioUrl}`,
        () => {
          if (score > 0) {
            DSBridge.call("addScore", score.toString());
          }
          DSBridge.call("exitWebView");
        }
      );
    } catch {
      if (score > 0) {
        DSBridge.call("addScore", score.toString());
      }
      DSBridge.call("exitWebView");
    }
  };

  const handleScratchStart = () => {
    setShowGuide(false);
  };

  const getEraseRadius = () => {
    const canvas = canvasRef.current;
    if (!canvas) return 20;
    // 考虑设备像素比调整半径
    const dpr = window.devicePixelRatio || 1;
    return (canvas.width > 500 ? 40 : 20) * dpr;
  };

  const getEventPosition = (e, rect) => {
    const touch = e.touches?.[0];
    const clientX = touch ? touch.clientX : e.clientX;
    const clientY = touch ? touch.clientY : e.clientY;

    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };

    // 获取设备像素比
    const dpr = window.devicePixelRatio || 1;

    // 计算相对于canvas的位置
    const canvasRect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / canvasRect.width;
    const scaleY = canvas.height / canvasRect.height;

    return {
      x: (clientX - canvasRect.left) * scaleX,
      y: (clientY - canvasRect.top) * scaleY,
    };
  };

  const drawLine = (ctx, x1, y1, x2, y2) => {
    const radius = getEraseRadius();

    ctx.beginPath();
    ctx.lineWidth = radius * 2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.moveTo(x1, y1);
    ctx.lineTo(x2, y2);
    ctx.stroke();

    // 绘制圆形确保连续性
    ctx.beginPath();
    ctx.arc(x2, y2, radius, 0, Math.PI * 2);
    ctx.fill();
  };

  const initCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    const container = canvas.parentElement;

    // 获取设备像素比
    const dpr = window.devicePixelRatio || 1;

    // 获取容器的实际尺寸
    const containerRect = container.getBoundingClientRect();
    const displayWidth = containerRect.width;
    const displayHeight = containerRect.height;

    // 设置canvas的实际尺寸（考虑设备像素比）
    canvas.width = displayWidth * dpr;
    canvas.height = displayHeight * dpr;

    // 设置canvas的显示尺寸
    canvas.style.width = displayWidth + "px";
    canvas.style.height = displayHeight + "px";

    // 缩放绘图上下文以匹配设备像素比
    ctx.scale(dpr, dpr);

    const img = new Image();
    img.src = require("@/images/new-game/scratch-game/coat.png");

    img.onload = () => {
      if (canvas) {
        // 绘制背景图片
        ctx.drawImage(img, 0, 0, displayWidth, displayHeight);
        // 设置混合模式为擦除
        ctx.globalCompositeOperation = "destination-out";
      }
    };
  };

  const createEventHandlers = () => {
    const canvas = canvasRef.current;
    if (!canvas) return {};

    const handleStart = (e) => {
      if (!isStartRef.current) return;

      // 阻止默认行为和事件冒泡
      e.preventDefault();
      e.stopPropagation();

      handleScratchStart();
      isDrawing.current = true;

      const rect = canvas.getBoundingClientRect();
      const position = getEventPosition(e, rect);
      lastPoint.current = position;

      // 绘制起始点
      const ctx = canvas.getContext("2d");
      const radius = getEraseRadius();
      const dpr = window.devicePixelRatio || 1;

      ctx.beginPath();
      ctx.arc(position.x / dpr, position.y / dpr, radius / dpr, 0, Math.PI * 2);
      ctx.fill();
    };

    const handleMove = (e) => {
      if (!isDrawing.current || !isStartRef.current) return;

      // 阻止默认行为和事件冒泡
      e.preventDefault();
      e.stopPropagation();

      const ctx = canvas.getContext("2d");
      const rect = canvas.getBoundingClientRect();
      const currentPoint = getEventPosition(e, rect);
      const dpr = window.devicePixelRatio || 1;

      drawLine(
        ctx,
        lastPoint.current.x / dpr,
        lastPoint.current.y / dpr,
        currentPoint.x / dpr,
        currentPoint.y / dpr
      );

      lastPoint.current = currentPoint;
    };

    const handleEnd = (e) => {
      if (!isStartRef.current) return;

      // 阻止默认行为和事件冒泡
      e.preventDefault();
      e.stopPropagation();

      isDrawing.current = false;
    };

    return { handleStart, handleMove, handleEnd };
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // 延迟初始化，确保容器尺寸已确定
    const initTimer = setTimeout(() => {
      initCanvas();
    }, 100);

    const { handleStart, handleMove, handleEnd } = createEventHandlers();

    const events = [
      // 鼠标事件
      ["mousedown", handleStart],
      ["mousemove", handleMove],
      ["mouseup", handleEnd],
      ["mouseleave", handleEnd],
      // 触摸事件
      ["touchstart", handleStart, { passive: false }],
      ["touchmove", handleMove, { passive: false }],
      ["touchend", handleEnd, { passive: false }],
      ["touchcancel", handleEnd, { passive: false }],
    ];

    events.forEach(([event, handler, options]) => {
      canvas.addEventListener(event, handler, options || false);
    });

    return () => {
      clearTimeout(initTimer);
      events.forEach(([event, handler]) => {
        canvas.removeEventListener(event, handler);
      });
    };
  }, [showScratchLayer]);

  // 添加窗口resize处理
  useEffect(() => {
    const handleResize = () => {
      // 延迟重新初始化canvas，确保布局完成
      setTimeout(() => {
        if (canvasRef.current) {
          initCanvas();
        }
      }, 100);
    };

    window.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleResize);
    };
  }, []);

  const handleTimeEnd = () => {
    DSBridge.call("stopSound", "bgm_sound");
    setShowScratchLayer(false);
    isStartRef.current = false;

    if (showGuide) {
      setShowGuide(false);
      DSBridge.call("playSVGA", "missed", () => {
        playAudioAndExit(explanationRef.current?.noAnswer);
      });
    } else {
      DSBridge.call("playSVGA", "outstanding", () => {
        playAudioAndExit(explanationRef.current?.correct, 10);
      });
    }
  };

  const initGame = async () => {
    try {
      // const res = {
      //   code: 200,
      //   status: true,
      //   data: {
      //     type: 14,
      //     question: "Time to practice!",
      //     question_zh: "",
      //     answer: "",
      //     image:
      //       "https://cdn03.myaitalk.vip/utalkrc/materials/slides/game_time_slides/game_3.jpg",
      //     practice_config: {
      //       type: "scratch",
      //       guide: "Scratch the card to find the word. Can you read it out loud",
      //       duration: 10,
      //       explanation: {
      //         correct:
      //           "Good job! The word is afternoon. It means after lunch and before evening.",
      //         incorrect:
      //           "Not quite. The word is afternoon. It's the time after lunch.",
      //         noAnswer:
      //           "The answer is afternoon. It comes after lunch and before evening.",
      //       },
      //       title: "What time of day is it",
      //       config: {
      //         image:
      //           "https://cdn03.myaitalk.vip/utalkrc/materials/slides/test_test/test_test-unit_1_tts_test_111301/practice_images/material_practice_image_ee1c9b4c-acdb-4d67-bddf-418efa811534.png",
      //       },
      //     },
      //   },
      //   message: "success",
      // };
      const res = await getMaterialsByGame({
        material_id: searchParams.get("material_id"),
        token: searchParams.get("token"),
        unit_id: searchParams.get("unit_id"),
      });
      if (res.code === 200) {
        const config = res.data?.practice_config;
        if (config?.type !== "scratch") {
          Toast.info("游戏类型错误，请点击左上角退出");
          return;
        }
        explanationRef.current = config?.explanation;
        setTitle(config?.title || "");
        setScratchImg(config?.config?.image || "");
        try {
          const audioUrl = await handleGetAudio(config?.guide);
          DSBridge.call(
            "playOnlineSound",
            `${process.env.REACT_APP_API_CDN}/${audioUrl}`,
            () => {
              downTimeRef.current?.startCountdown(config?.duration);
              isStartRef.current = true;
            }
          );
        } catch {
          downTimeRef.current?.startCountdown(config?.duration);
          isStartRef.current = true;
        }
      } else {
        Toast.info("请求游戏数据失败，请点击左上角退出");
      }
    } catch {
      Toast.info("验证游戏数据失败，请点击左上角退出");
    }
  };

  useEffect(() => {
    DSBridge.call("playSound", "bgm_sound");
    DSBridge.call("playSound", "start_sound");
    setTimeout(() => {
      setIsVisible(false);
    }, 1000);
    initGame();
    return () => {
      DSBridge.call("stopSound", "bgm_sound");
    };
  }, []);

  return (
    <div className={styles["scratch-game-container"]}>
      {isVisible && (
        <div className={styles["start-box"]}>
          <img
            src={require("@/images/new-game/common/start.png")}
            className={styles["start-cover"]}
          />
        </div>
      )}
      <div className={`${isVisible ? styles["visibility-box"] : ""}`}>
        <div className={styles["back-top"]} />
        <div className={styles["back-bottom"]} />
        <div className={styles["center"]}>
          <div className={styles["center-title"]}>{title}</div>
          <div className={styles["center-content"]}>
            <div className={styles["prize-content"]}>
              <img className={styles["prize-content-img"]} src={scratchImg} />
            </div>
            {showScratchLayer && (
              <canvas ref={canvasRef} className={styles["scratch-layer"]} />
            )}
            {showGuide && (
              <img
                src={require("@/images/new-game/scratch-game/finger.png")}
                className={`${styles["finger"]} ${styles["animate"]}`}
              />
            )}
          </div>
        </div>
        <DownTime ref={downTimeRef} onTimeEnd={handleTimeEnd} />
        <NoticeText text="Scratch  刮一刮，看看藏的是什么" />
      </div>
      <div className={styles["close-icon"]} onClick={handleClose}>
        <img
          className={styles["close-icon-img"]}
          src={require("@/images/new-game/common/close.png")}
        />
      </div>
    </div>
  );
};

export default ScratchGame;
